/**
 * MiniMod - Synthétiseur Modulaire Complet
 *
 * Un vrai système modulaire avec patching visuel.
 * Utilise le séquenceur principal 16 steps comme les autres instruments.
 *
 * Modules:
 * - VCO 1/2/3: Oscillateurs
 * - LFO 1/2: Oscillateurs basse fréquence
 * - NOISE: Générateur de bruit
 * - ENV 1/2: Enveloppes ADSR
 * - VCF: Filtre résonant
 * - VCA 1/2: Amplificateurs
 * - MIX: Mixer 4 canaux
 * - MULT: Splitter de signal
 * - RING: Ring Modulator
 * - S&H: Sample & Hold
 * - SLEW: Slew Limiter (portamento)
 * - OUTPUT: Sortie vers mixer
 */

import * as Tone from 'tone';

export class MiniMod {
    constructor(audioEngine, options = {}) {
        this.audioEngine = audioEngine;
        this.isPowered = true;
        this.mixerChannel = null;

        // Paramètres par défaut
        this.params = {
            // VCOs (octave: -2 to +2, detune in cents)
            vco1Freq: 220, vco1Waveform: 'sawtooth', vco1Detune: 0, vco1Octave: 0,
            vco2Freq: 220, vco2Waveform: 'square', vco2Detune: 5, vco2Octave: 0,
            vco3Freq: 110, vco3Waveform: 'sawtooth', vco3Detune: -5, vco3Octave: -1,
            // LFOs
            lfo1Rate: 2, lfo1Waveform: 'sine',
            lfo2Rate: 0.5, lfo2Waveform: 'triangle',
            // VCF
            vcfCutoff: 2000, vcfResonance: 5, vcfType: 'lowpass',
            // VCAs (0 par défaut - ouvert par envelope)
            vca1Level: 0, vca2Level: 0,
            // ENVs
            env1Attack: 0.01, env1Decay: 0.2, env1Sustain: 0.5, env1Release: 0.3,
            env2Attack: 0.05, env2Decay: 0.3, env2Sustain: 0.3, env2Release: 0.5,
            // Mixer
            mix1Level: 1, mix2Level: 0, mix3Level: 0, mix4Level: 0,
            // Slew
            slewTime: 0.1
        };

        // Current note for VCO tracking
        this.currentNote = null;

        // Connexions actives
        this.connections = [];

        // Jacks
        this.jacks = {};

        this.setupAudio();
    }

    setupAudio() {
        this.mixerChannel = this.audioEngine.getMixerChannelInput('minimod');

        // === VCO 1 ===
        this.vco1 = new Tone.Oscillator({
            frequency: this.params.vco1Freq,
            type: this.params.vco1Waveform,
            detune: this.params.vco1Detune
        });
        this.vco1Gain = new Tone.Gain(1);
        this.vco1.connect(this.vco1Gain);
        this.vco1FreqMod = new Tone.Gain(200);
        this.vco1FreqMod.connect(this.vco1.frequency);

        // === VCO 2 ===
        this.vco2 = new Tone.Oscillator({
            frequency: this.params.vco2Freq,
            type: this.params.vco2Waveform,
            detune: this.params.vco2Detune
        });
        this.vco2Gain = new Tone.Gain(1);
        this.vco2.connect(this.vco2Gain);
        this.vco2FreqMod = new Tone.Gain(200);
        this.vco2FreqMod.connect(this.vco2.frequency);

        // === VCO 3 ===
        this.vco3 = new Tone.Oscillator({
            frequency: this.params.vco3Freq,
            type: this.params.vco3Waveform,
            detune: this.params.vco3Detune
        });
        this.vco3Gain = new Tone.Gain(1);
        this.vco3.connect(this.vco3Gain);
        this.vco3FreqMod = new Tone.Gain(200);
        this.vco3FreqMod.connect(this.vco3.frequency);

        // === LFO 1 ===
        this.lfo1 = new Tone.LFO({
            frequency: this.params.lfo1Rate,
            type: this.params.lfo1Waveform,
            min: -1, max: 1
        });
        this.lfo1Gain = new Tone.Gain(1);
        this.lfo1.connect(this.lfo1Gain);

        // === LFO 2 ===
        this.lfo2 = new Tone.LFO({
            frequency: this.params.lfo2Rate,
            type: this.params.lfo2Waveform,
            min: -1, max: 1
        });
        this.lfo2Gain = new Tone.Gain(1);
        this.lfo2.connect(this.lfo2Gain);

        // === NOISE ===
        this.noise = new Tone.Noise('white');
        this.noiseGain = new Tone.Gain(0.5);
        this.noise.connect(this.noiseGain);

        // === ENV 1 ===
        this.env1 = new Tone.Envelope({
            attack: this.params.env1Attack,
            decay: this.params.env1Decay,
            sustain: this.params.env1Sustain,
            release: this.params.env1Release
        });
        this.env1Gain = new Tone.Gain(0);
        this.env1.connect(this.env1Gain.gain);
        const env1Const = new Tone.Signal(1);
        env1Const.connect(this.env1Gain);

        // === ENV 2 ===
        this.env2 = new Tone.Envelope({
            attack: this.params.env2Attack,
            decay: this.params.env2Decay,
            sustain: this.params.env2Sustain,
            release: this.params.env2Release
        });
        this.env2Gain = new Tone.Gain(0);
        this.env2.connect(this.env2Gain.gain);
        const env2Const = new Tone.Signal(1);
        env2Const.connect(this.env2Gain);

        // === VCF ===
        this.vcf = new Tone.Filter({
            frequency: this.params.vcfCutoff,
            type: this.params.vcfType,
            rolloff: -24,
            Q: this.params.vcfResonance
        });
        this.vcfGain = new Tone.Gain(1);
        this.vcf.connect(this.vcfGain);
        this.vcfModGain = new Tone.Gain(2000);
        this.vcfModGain.connect(this.vcf.frequency);

        // === VCA 1 ===
        this.vca1 = new Tone.Gain(this.params.vca1Level);
        this.vca1ModGain = new Tone.Gain(1);  // Full envelope -> full VCA open
        this.vca1ModGain.connect(this.vca1.gain);

        // === VCA 2 ===
        this.vca2 = new Tone.Gain(this.params.vca2Level);
        this.vca2ModGain = new Tone.Gain(1);  // Full envelope -> full VCA open
        this.vca2ModGain.connect(this.vca2.gain);

        // === MIXER (4 channels) ===
        this.mix1 = new Tone.Gain(this.params.mix1Level);
        this.mix2 = new Tone.Gain(this.params.mix2Level);
        this.mix3 = new Tone.Gain(this.params.mix3Level);
        this.mix4 = new Tone.Gain(this.params.mix4Level);
        this.mixOut = new Tone.Gain(1);
        this.mix1.connect(this.mixOut);
        this.mix2.connect(this.mixOut);
        this.mix3.connect(this.mixOut);
        this.mix4.connect(this.mixOut);

        // === MULT (1 in, 3 out - passive splitter) ===
        this.multIn = new Tone.Gain(1);
        this.mult1 = new Tone.Gain(1);
        this.mult2 = new Tone.Gain(1);
        this.mult3 = new Tone.Gain(1);
        this.multIn.connect(this.mult1);
        this.multIn.connect(this.mult2);
        this.multIn.connect(this.mult3);

        // === RING MODULATOR ===
        this.ringMult = new Tone.Multiply();
        this.ringCarrier = new Tone.Gain(1); // Input A
        this.ringMod = new Tone.Gain(1); // Input B
        this.ringOut = new Tone.Gain(1);
        this.ringCarrier.connect(this.ringMult);
        this.ringMod.connect(this.ringMult.factor);
        this.ringMult.connect(this.ringOut);

        // === SAMPLE & HOLD ===
        // S&H samples the input when triggered
        this.shInput = new Tone.Gain(1);
        this.shOutput = new Tone.Signal(0);
        this.shValue = 0;

        // === SLEW LIMITER ===
        this.slewIn = new Tone.Signal(0);
        this.slewOut = new Tone.Signal(0);
        // Use Tone's built-in ramp
        this.slewFollower = new Tone.Follower(this.params.slewTime);
        this.slewIn.connect(this.slewFollower);
        this.slewFollower.connect(this.slewOut);

        // === OUTPUT ===
        this.outputGain = new Tone.Gain(1);
        this.outputGain.connect(this.mixerChannel);

        // Define all jacks
        this._defineJacks();

        // Start oscillators
        this.vco1.start();
        this.vco2.start();
        this.vco3.start();
        this.lfo1.start();
        this.lfo2.start();
        this.noise.start();

        // Default patching
        this._setupDefaultPatching();
    }

    _defineJacks() {
        this.jacks = {
            // VCO 1
            vco1Out: { type: 'output', module: 'vco1', label: 'OUT', node: this.vco1Gain },
            vco1Fm: { type: 'input', module: 'vco1', label: 'FM', node: this.vco1FreqMod },
            // VCO 2
            vco2Out: { type: 'output', module: 'vco2', label: 'OUT', node: this.vco2Gain },
            vco2Fm: { type: 'input', module: 'vco2', label: 'FM', node: this.vco2FreqMod },
            // VCO 3
            vco3Out: { type: 'output', module: 'vco3', label: 'OUT', node: this.vco3Gain },
            vco3Fm: { type: 'input', module: 'vco3', label: 'FM', node: this.vco3FreqMod },
            // LFO 1
            lfo1Out: { type: 'output', module: 'lfo1', label: 'OUT', node: this.lfo1Gain },
            // LFO 2
            lfo2Out: { type: 'output', module: 'lfo2', label: 'OUT', node: this.lfo2Gain },
            // Noise
            noiseOut: { type: 'output', module: 'noise', label: 'OUT', node: this.noiseGain },
            // ENV 1
            env1Out: { type: 'output', module: 'env1', label: 'OUT', node: this.env1Gain },
            // ENV 2
            env2Out: { type: 'output', module: 'env2', label: 'OUT', node: this.env2Gain },
            // VCF
            vcfIn: { type: 'input', module: 'vcf', label: 'IN', node: this.vcf },
            vcfMod: { type: 'input', module: 'vcf', label: 'MOD', node: this.vcfModGain },
            vcfOut: { type: 'output', module: 'vcf', label: 'OUT', node: this.vcfGain },
            // VCA 1
            vca1In: { type: 'input', module: 'vca1', label: 'IN', node: this.vca1 },
            vca1Mod: { type: 'input', module: 'vca1', label: 'MOD', node: this.vca1ModGain },
            vca1Out: { type: 'output', module: 'vca1', label: 'OUT', node: this.vca1 },
            // VCA 2
            vca2In: { type: 'input', module: 'vca2', label: 'IN', node: this.vca2 },
            vca2Mod: { type: 'input', module: 'vca2', label: 'MOD', node: this.vca2ModGain },
            vca2Out: { type: 'output', module: 'vca2', label: 'OUT', node: this.vca2 },
            // Mixer
            mix1In: { type: 'input', module: 'mix', label: '1', node: this.mix1 },
            mix2In: { type: 'input', module: 'mix', label: '2', node: this.mix2 },
            mix3In: { type: 'input', module: 'mix', label: '3', node: this.mix3 },
            mix4In: { type: 'input', module: 'mix', label: '4', node: this.mix4 },
            mixOut: { type: 'output', module: 'mix', label: 'OUT', node: this.mixOut },
            // Mult
            multIn: { type: 'input', module: 'mult', label: 'IN', node: this.multIn },
            mult1Out: { type: 'output', module: 'mult', label: '1', node: this.mult1 },
            mult2Out: { type: 'output', module: 'mult', label: '2', node: this.mult2 },
            mult3Out: { type: 'output', module: 'mult', label: '3', node: this.mult3 },
            // Ring Mod
            ringCarrierIn: { type: 'input', module: 'ring', label: 'CAR', node: this.ringCarrier },
            ringModIn: { type: 'input', module: 'ring', label: 'MOD', node: this.ringMod },
            ringOut: { type: 'output', module: 'ring', label: 'OUT', node: this.ringOut },
            // S&H
            shIn: { type: 'input', module: 'sh', label: 'IN', node: this.shInput },
            shOut: { type: 'output', module: 'sh', label: 'OUT', node: this.shOutput },
            // Slew
            slewIn: { type: 'input', module: 'slew', label: 'IN', node: this.slewIn },
            slewOut: { type: 'output', module: 'slew', label: 'OUT', node: this.slewOut },
            // Main Output
            mainOut: { type: 'input', module: 'output', label: 'IN', node: this.outputGain }
        };
    }

    _setupDefaultPatching() {
        // Classic subtractive: VCO1 + VCO2 -> MIX -> VCF -> VCA1 -> OUT
        this.connect('vco1Out', 'mix1In');
        this.connect('vco2Out', 'mix2In');
        this.connect('mixOut', 'vcfIn');
        this.connect('vcfOut', 'vca1In');
        this.connect('vca1Out', 'mainOut');
        // ENV1 -> VCA1 mod
        this.connect('env1Out', 'vca1Mod');
        // LFO1 -> VCF mod (subtle)
        this.connect('lfo1Out', 'vcfMod');
    }

    // === CONNECTION MANAGEMENT ===

    connect(sourceId, destId) {
        const source = this.jacks[sourceId];
        const dest = this.jacks[destId];

        if (!source || !dest) {
            console.warn(`MiniMod: Invalid jacks: ${sourceId} -> ${destId}`);
            return false;
        }

        if (source.type !== 'output' || dest.type !== 'input') {
            console.warn(`MiniMod: Can only connect output -> input`);
            return false;
        }

        const existing = this.connections.find(c => c.source === sourceId && c.dest === destId);
        if (existing) return false;

        try {
            source.node.connect(dest.node);
            this.connections.push({ source: sourceId, dest: destId });
            return true;
        } catch (e) {
            console.error(`MiniMod: Connection failed:`, e);
            return false;
        }
    }

    disconnect(sourceId, destId) {
        const source = this.jacks[sourceId];
        const dest = this.jacks[destId];

        if (!source || !dest) return false;

        const index = this.connections.findIndex(c => c.source === sourceId && c.dest === destId);
        if (index === -1) return false;

        try {
            source.node.disconnect(dest.node);
            this.connections.splice(index, 1);
            return true;
        } catch (e) {
            console.error(`MiniMod: Disconnect failed:`, e);
            return false;
        }
    }

    disconnectAll() {
        [...this.connections].forEach(conn => {
            this.disconnect(conn.source, conn.dest);
        });
    }

    resetPatching() {
        this.disconnectAll();
        this._setupDefaultPatching();
    }

    getConnections() {
        return [...this.connections];
    }

    getJacks() {
        return this.jacks;
    }

    // === TRIGGER METHODS ===

    triggerEnv1(duration = '8n') {
        this.env1.triggerAttackRelease(Tone.Time(duration).toSeconds());
    }

    triggerEnv2(duration = '8n') {
        this.env2.triggerAttackRelease(Tone.Time(duration).toSeconds());
    }

    triggerBoth(duration = '8n') {
        const dur = Tone.Time(duration).toSeconds();
        this.env1.triggerAttackRelease(dur);
        this.env2.triggerAttackRelease(dur);
    }

    env1Attack() { this.env1.triggerAttack(); }
    env1Release() { this.env1.triggerRelease(); }
    env2Attack() { this.env2.triggerAttack(); }
    env2Release() { this.env2.triggerRelease(); }

    // === SAMPLE & HOLD ===

    sampleAndHold() {
        // Sample current value from shInput (simplified - would need analyser for real implementation)
        // For now, use a random value as demo
        this.shValue = Math.random() * 2 - 1;
        this.shOutput.value = this.shValue;
    }

    // === TRIGGERING (for main sequencer) ===

    /**
     * Trigger a note with duration (called by main sequencer)
     * @param {string} note - Note like 'C3', 'D#4', etc.
     * @param {string|number} duration - Duration like '16n' or seconds
     * @param {number} time - Tone.js time
     */
    triggerAttackRelease(note, duration, time) {
        const freq = Tone.Frequency(note).toFrequency();
        const now = time || Tone.now();

        // Set all VCO frequencies with octave offset and detune
        this.vco1.frequency.setValueAtTime(
            this._calcFreq(freq, this.params.vco1Octave, this.params.vco1Detune),
            now
        );
        this.vco2.frequency.setValueAtTime(
            this._calcFreq(freq, this.params.vco2Octave, this.params.vco2Detune),
            now
        );
        this.vco3.frequency.setValueAtTime(
            this._calcFreq(freq, this.params.vco3Octave, this.params.vco3Detune),
            now
        );

        // Trigger both envelopes
        const dur = Tone.Time(duration).toSeconds();
        this.env1.triggerAttackRelease(dur, now);
        this.env2.triggerAttackRelease(dur, now);

        this.currentNote = note;
    }

    /**
     * Calculate frequency with octave offset and detune
     */
    _calcFreq(baseFreq, octaveOffset, detuneCents) {
        const octaveMult = Math.pow(2, octaveOffset);
        const detuneMult = Math.pow(2, detuneCents / 1200);
        return baseFreq * octaveMult * detuneMult;
    }

    // === SETTERS ===

    // VCO 1
    setVco1Freq(v) { this.params.vco1Freq = v; this.vco1.frequency.value = v; }
    setVco1Waveform(v) { this.params.vco1Waveform = v; this.vco1.type = v; }
    setVco1Detune(v) { this.params.vco1Detune = v; this.vco1.detune.value = v; }
    setVco1Octave(v) { this.params.vco1Octave = v; }

    // VCO 2
    setVco2Freq(v) { this.params.vco2Freq = v; this.vco2.frequency.value = v; }
    setVco2Waveform(v) { this.params.vco2Waveform = v; this.vco2.type = v; }
    setVco2Detune(v) { this.params.vco2Detune = v; this.vco2.detune.value = v; }
    setVco2Octave(v) { this.params.vco2Octave = v; }

    // VCO 3
    setVco3Freq(v) { this.params.vco3Freq = v; this.vco3.frequency.value = v; }
    setVco3Waveform(v) { this.params.vco3Waveform = v; this.vco3.type = v; }
    setVco3Detune(v) { this.params.vco3Detune = v; this.vco3.detune.value = v; }
    setVco3Octave(v) { this.params.vco3Octave = v; }

    // LFOs
    setLfo1Rate(v) { this.params.lfo1Rate = v; this.lfo1.frequency.value = v; }
    setLfo1Waveform(v) { this.params.lfo1Waveform = v; this.lfo1.type = v; }
    setLfo2Rate(v) { this.params.lfo2Rate = v; this.lfo2.frequency.value = v; }
    setLfo2Waveform(v) { this.params.lfo2Waveform = v; this.lfo2.type = v; }

    // VCF
    setVcfCutoff(v) { this.params.vcfCutoff = v; this.vcf.frequency.value = v; }
    setVcfResonance(v) { this.params.vcfResonance = v; this.vcf.Q.value = v; }

    // VCAs
    setVca1Level(v) { this.params.vca1Level = v; }
    setVca2Level(v) { this.params.vca2Level = v; }

    // ENVs
    setEnv1Attack(v) { this.params.env1Attack = v; this.env1.attack = v; }
    setEnv1Decay(v) { this.params.env1Decay = v; this.env1.decay = v; }
    setEnv1Sustain(v) { this.params.env1Sustain = v; this.env1.sustain = v; }
    setEnv1Release(v) { this.params.env1Release = v; this.env1.release = v; }
    setEnv2Attack(v) { this.params.env2Attack = v; this.env2.attack = v; }
    setEnv2Decay(v) { this.params.env2Decay = v; this.env2.decay = v; }
    setEnv2Sustain(v) { this.params.env2Sustain = v; this.env2.sustain = v; }
    setEnv2Release(v) { this.params.env2Release = v; this.env2.release = v; }

    // Mixer
    setMix1Level(v) { this.params.mix1Level = v; this.mix1.gain.value = v; }
    setMix2Level(v) { this.params.mix2Level = v; this.mix2.gain.value = v; }
    setMix3Level(v) { this.params.mix3Level = v; this.mix3.gain.value = v; }
    setMix4Level(v) { this.params.mix4Level = v; this.mix4.gain.value = v; }

    // Slew
    setSlewTime(v) { this.params.slewTime = v; this.slewFollower.smoothing = v; }

    // === GETTERS ===
    get vco1Freq() { return this.params.vco1Freq; }
    get vco1Waveform() { return this.params.vco1Waveform; }
    get vco2Freq() { return this.params.vco2Freq; }
    get vco2Waveform() { return this.params.vco2Waveform; }
    get vco3Freq() { return this.params.vco3Freq; }
    get vco3Waveform() { return this.params.vco3Waveform; }
    get lfo1Rate() { return this.params.lfo1Rate; }
    get lfo2Rate() { return this.params.lfo2Rate; }
    get vcfCutoff() { return this.params.vcfCutoff; }
    get vcfResonance() { return this.params.vcfResonance; }

    // === PRESETS ===

    loadPreset(preset) {
        if (!preset) return;

        // Disconnect all first
        this.disconnectAll();

        // Apply parameters
        Object.keys(preset.params || {}).forEach(key => {
            if (this.params[key] !== undefined) {
                this.params[key] = preset.params[key];
            }
        });

        // Apply to audio nodes
        if (preset.params) {
            const p = preset.params;
            if (p.vco1Freq) this.vco1.frequency.value = p.vco1Freq;
            if (p.vco1Waveform) this.vco1.type = p.vco1Waveform;
            if (p.vco2Freq) this.vco2.frequency.value = p.vco2Freq;
            if (p.vco2Waveform) this.vco2.type = p.vco2Waveform;
            if (p.vco3Freq) this.vco3.frequency.value = p.vco3Freq;
            if (p.vco3Waveform) this.vco3.type = p.vco3Waveform;
            if (p.lfo1Rate) this.lfo1.frequency.value = p.lfo1Rate;
            if (p.lfo2Rate) this.lfo2.frequency.value = p.lfo2Rate;
            if (p.vcfCutoff) this.vcf.frequency.value = p.vcfCutoff;
            if (p.vcfResonance) this.vcf.Q.value = p.vcfResonance;
            if (p.mix1Level) this.mix1.gain.value = p.mix1Level;
            if (p.mix2Level) this.mix2.gain.value = p.mix2Level;
            if (p.mix3Level) this.mix3.gain.value = p.mix3Level;
            if (p.mix4Level) this.mix4.gain.value = p.mix4Level;
        }

        // Apply connections
        (preset.connections || []).forEach(conn => {
            this.connect(conn.source, conn.dest);
        });
    }

    // === POWER ===

    setPower(on) {
        if (on === this.isPowered) return;
        this.isPowered = on;

        if (on) {
            this.vco1.start();
            this.vco2.start();
            this.vco3.start();
            this.lfo1.start();
            this.lfo2.start();
            this.noise.start();
            this.outputGain.connect(this.mixerChannel);
        } else {
            this.vco1.stop();
            this.vco2.stop();
            this.vco3.stop();
            this.lfo1.stop();
            this.lfo2.stop();
            this.noise.stop();
            this.outputGain.disconnect();
        }
    }

    getPower() {
        return this.isPowered;
    }
}

// === PRESETS ===

export const MINIMOD_PRESETS = {
    'Init': {
        params: {
            vco1Freq: 220, vco1Waveform: 'sawtooth',
            vco2Freq: 220, vco2Waveform: 'square',
            vco3Freq: 110, vco3Waveform: 'sawtooth',
            lfo1Rate: 2, lfo2Rate: 0.5,
            vcfCutoff: 2000, vcfResonance: 5,
            mix1Level: 1, mix2Level: 0, mix3Level: 0, mix4Level: 0
        },
        connections: [
            { source: 'vco1Out', dest: 'mix1In' },
            { source: 'mixOut', dest: 'vcfIn' },
            { source: 'vcfOut', dest: 'vca1In' },
            { source: 'vca1Out', dest: 'mainOut' },
            { source: 'env1Out', dest: 'vca1Mod' }
        ]
    },
    'Fat Bass': {
        params: {
            vco1Freq: 55, vco1Waveform: 'sawtooth',
            vco2Freq: 55, vco2Waveform: 'square', vco2Detune: 7,
            vco3Freq: 27.5, vco3Waveform: 'sawtooth',
            lfo1Rate: 0.3,
            vcfCutoff: 400, vcfResonance: 8,
            mix1Level: 0.8, mix2Level: 0.6, mix3Level: 0.4, mix4Level: 0
        },
        connections: [
            { source: 'vco1Out', dest: 'mix1In' },
            { source: 'vco2Out', dest: 'mix2In' },
            { source: 'vco3Out', dest: 'mix3In' },
            { source: 'mixOut', dest: 'vcfIn' },
            { source: 'vcfOut', dest: 'vca1In' },
            { source: 'vca1Out', dest: 'mainOut' },
            { source: 'env1Out', dest: 'vca1Mod' },
            { source: 'env1Out', dest: 'vcfMod' }
        ]
    },
    'Analog Lead': {
        params: {
            vco1Freq: 440, vco1Waveform: 'sawtooth',
            vco2Freq: 440, vco2Waveform: 'sawtooth', vco2Detune: 10,
            lfo1Rate: 5,
            vcfCutoff: 3000, vcfResonance: 12,
            mix1Level: 1, mix2Level: 0.7, mix3Level: 0, mix4Level: 0
        },
        connections: [
            { source: 'vco1Out', dest: 'mix1In' },
            { source: 'vco2Out', dest: 'mix2In' },
            { source: 'mixOut', dest: 'vcfIn' },
            { source: 'vcfOut', dest: 'vca1In' },
            { source: 'vca1Out', dest: 'mainOut' },
            { source: 'env1Out', dest: 'vca1Mod' },
            { source: 'lfo1Out', dest: 'vco1Fm' }
        ]
    },
    'Ring Mod Madness': {
        params: {
            vco1Freq: 220, vco1Waveform: 'sine',
            vco2Freq: 277, vco2Waveform: 'sine',
            vcfCutoff: 5000, vcfResonance: 3
        },
        connections: [
            { source: 'vco1Out', dest: 'ringCarrierIn' },
            { source: 'vco2Out', dest: 'ringModIn' },
            { source: 'ringOut', dest: 'vcfIn' },
            { source: 'vcfOut', dest: 'vca1In' },
            { source: 'vca1Out', dest: 'mainOut' },
            { source: 'env1Out', dest: 'vca1Mod' }
        ]
    },
    'Noise Percussion': {
        params: {
            vcfCutoff: 1500, vcfResonance: 15,
            env1Attack: 0.001, env1Decay: 0.15, env1Sustain: 0, env1Release: 0.1
        },
        connections: [
            { source: 'noiseOut', dest: 'vcfIn' },
            { source: 'vcfOut', dest: 'vca1In' },
            { source: 'vca1Out', dest: 'mainOut' },
            { source: 'env1Out', dest: 'vca1Mod' },
            { source: 'env1Out', dest: 'vcfMod' }
        ]
    },
    'Sci-Fi': {
        params: {
            vco1Freq: 80, vco1Waveform: 'sawtooth',
            vco2Freq: 81, vco2Waveform: 'sawtooth',
            lfo1Rate: 8, lfo2Rate: 0.1,
            vcfCutoff: 800, vcfResonance: 18
        },
        connections: [
            { source: 'vco1Out', dest: 'mix1In' },
            { source: 'vco2Out', dest: 'mix2In' },
            { source: 'mixOut', dest: 'vcfIn' },
            { source: 'vcfOut', dest: 'vca1In' },
            { source: 'vca1Out', dest: 'mainOut' },
            { source: 'env1Out', dest: 'vca1Mod' },
            { source: 'lfo1Out', dest: 'vcfMod' },
            { source: 'lfo2Out', dest: 'vco1Fm' },
            { source: 'lfo2Out', dest: 'vco2Fm' }
        ]
    },
    'Plucky': {
        params: {
            vco1Freq: 220, vco1Waveform: 'sawtooth',
            vco2Freq: 220, vco2Waveform: 'square', vco2Detune: 7,
            vcfCutoff: 1200, vcfResonance: 10,
            env1Attack: 0.001, env1Decay: 0.2, env1Sustain: 0, env1Release: 0.1,
            mix1Level: 0.8, mix2Level: 0.4
        },
        connections: [
            { source: 'vco1Out', dest: 'mix1In' },
            { source: 'vco2Out', dest: 'mix2In' },
            { source: 'mixOut', dest: 'vcfIn' },
            { source: 'vcfOut', dest: 'vca1In' },
            { source: 'vca1Out', dest: 'mainOut' },
            { source: 'env1Out', dest: 'vca1Mod' },
            { source: 'env1Out', dest: 'vcfMod' }
        ]
    },
    'Drone': {
        params: {
            vco1Freq: 55, vco1Waveform: 'sawtooth',
            vco2Freq: 55.5, vco2Waveform: 'sawtooth',
            vco3Freq: 82.5, vco3Waveform: 'triangle',
            lfo1Rate: 0.05, lfo2Rate: 0.08,
            vcfCutoff: 600, vcfResonance: 6,
            mix1Level: 0.7, mix2Level: 0.7, mix3Level: 0.5
        },
        connections: [
            { source: 'vco1Out', dest: 'mix1In' },
            { source: 'vco2Out', dest: 'mix2In' },
            { source: 'vco3Out', dest: 'mix3In' },
            { source: 'mixOut', dest: 'vcfIn' },
            { source: 'vcfOut', dest: 'vca1In' },
            { source: 'vca1Out', dest: 'mainOut' },
            { source: 'lfo1Out', dest: 'vcfMod' },
            { source: 'lfo2Out', dest: 'vco1Fm' }
        ]
    }
};
